import { getRouteApi, Link } from '@tanstack/react-router'
import { Briefcase, Clock, Search, Users } from 'lucide-react'
import { useEffect, useState } from 'react'

import { PageIntro } from '@/components/page-intro'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/field'
import { JobStatusBadge } from '@/components/ui/status'
import { Skeleton } from '@/components/ui/skeleton'
import { useJobs } from '@/features/marketplace/api'
import { formatNumber, formatRelative, formatRupiah } from '@/lib/format'

const route = getRouteApi('/public/proyek')

/** Papan proyek publik: daftar kebutuhan yang sedang mencari tenaga ahli. */
export function JobBoardPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const [draft, setDraft] = useState(search.q ?? '')

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((search.q ?? '') === draft) return
      navigate({ search: (previous) => ({ ...previous, q: draft || undefined, page: 1 }), replace: true })
    }, 350)
    return () => window.clearTimeout(timer)
  }, [draft, navigate, search.q])

  const { data, isLoading } = useJobs({ q: search.q, skill: search.skill, page: search.page })
  const jobs = data?.job_posts ?? []
  const meta = data?.meta

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
      <PageIntro
        accent="proyek"
        eyebrow="Proyek escrow"
        icon={<Briefcase className="size-3.5" />}
        title="Papan proyek"
        description="Kebutuhan nyata dari klien yang siap mengunci dana di escrow sebelum pekerjaan dimulai."
      />

      <div className="relative mt-8 max-w-xl">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Cari proyek berdasarkan judul atau kebutuhan"
          aria-label="Cari proyek"
          className="pl-10"
        />
      </div>

      <p className="mt-6 text-sm text-muted" aria-live="polite">
        {isLoading ? 'Memuat proyek…' : `${formatNumber(meta?.total ?? 0)} proyek terbuka`}
      </p>

      {isLoading ? (
        <div className="mt-4 space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <Card className="mt-4">
          <EmptyState illustration="box"
            icon={<Briefcase className="size-6" />}
            title="Belum ada proyek terbuka"
            description="Proyek baru muncul setiap saat. Simpan halaman ini dan periksa kembali nanti."
          />
        </Card>
      ) : (
        <div className="mt-4 space-y-3">
          {jobs.map((job) => (
            <Card key={job.id} className="transition hover:border-brand/40">
              <CardBody>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <JobStatusBadge status={job.status} />
                      <Badge tone="brand">{job.category}</Badge>
                      <span className="text-xs text-subtle">{formatRelative(job.created_at)}</span>
                    </div>

                    <Link to="/proyek/$jobId" params={{ jobId: job.id }} className="mt-2 block">
                      <h2 className="text-lg font-bold text-ink transition hover:text-brand">{job.title}</h2>
                    </Link>

                    <p className="clamp-2 mt-1.5 text-sm text-muted">{job.description}</p>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {job.skills.map((skill) => (
                        <span key={skill} className="rounded-full bg-sunken px-2.5 py-1 text-xs font-medium text-muted">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <p className="text-xs text-subtle">Anggaran</p>
                    <p className="text-lg font-extrabold tabular-nums text-ink">
                      {formatRupiah(job.budget_min)} – {formatRupiah(job.budget_max)}
                    </p>
                    <div className="mt-2 flex items-center justify-end gap-3 text-xs text-subtle">
                      <span className="inline-flex items-center gap-1">
                        <Users className="size-3.5" />
                        {job.proposal_count} proposal
                      </span>
                      {job.duration_days ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="size-3.5" />
                          {job.duration_days} hari
                        </span>
                      ) : null}
                    </div>
                    <Link to="/proyek/$jobId" params={{ jobId: job.id }} className="mt-3 inline-block">
                      <Button size="sm">Lihat detail</Button>
                    </Link>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {meta && meta.total_pages > 1 ? (
        <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Navigasi halaman">
          <Button
            variant="outline"
            size="sm"
            disabled={meta.page <= 1}
            onClick={() => navigate({ search: (previous) => ({ ...previous, page: meta.page - 1 }) })}
          >
            Sebelumnya
          </Button>
          <span className="px-3 text-sm text-muted">
            Halaman {meta.page} dari {meta.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={meta.page >= meta.total_pages}
            onClick={() => navigate({ search: (previous) => ({ ...previous, page: meta.page + 1 }) })}
          >
            Berikutnya
          </Button>
        </nav>
      ) : null}
    </div>
  )
}
